import React from "react";
import { ChevronRight12Regular } from "@fluentui/react-icons";
import { FocusRestoreContext, TreeActionsTabbableContext } from "./SideMenuContexts";

export interface TreeViewProps<T> {
    items: T[];
    /**
     * Id of an item in the expanded state (`openItems`), so keep it stable across reloads. `depth` is 0 for the items
     * passed in `items`. Ids may repeat: items with the same id share their expanded state, but are still separate rows.
     */
    getId: (item: T, parent: T | undefined, depth: number) => string;
    getChildren?: (item: T) => T[] | undefined;
    renderLabel: (item: T, parent: T | undefined, depth: number) => React.ReactNode;
    /** Shows the label in the secondary text colour, e.g. for items that offer nothing to do. */
    isMuted?: (item: T, parent: T | undefined, depth: number) => boolean;
    /**
     * Content of the row's actions slot, usually an ActionMenu. When this prop is set every row reserves the slot,
     * also rows it returns null for, so labels line up and hovering never moves or re-wraps them.
     */
    renderActions?: (item: T, parent: T | undefined, depth: number) => React.ReactNode;
    /** Expanded item ids. Leave out to let the tree keep its own state. */
    openItems?: ReadonlySet<string>;
    onOpenChange?: (openItems: Set<string>) => void;
    /** Extra left padding per nesting level, in pixels. */
    indent?: number;
    ariaLabel?: string;
    className?: string;
}

interface VisibleItem {
    key: string;
    id: string;
    parentKey?: string;
    hasChildren: boolean;
}

/** Left padding of a depth 0 row; deeper rows add `indent` per level. */
const ROW_START_PADDING = 4;
/** Half the chevron slot, so a guide line runs under the middle of its parent's chevron. */
const CHEVRON_CENTER = 12;

const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

// Text colours are variables on the row, so the label, chevron and "..." all switch with the row's highlighted state
// (hover, open menu). Every pair is from one Fluent token family, which keeps it readable in all Teams themes; in high
// contrast the highlight is cyan with black text. Only devices that can hover get hover styles, so a tap on a touch
// screen doesn't leave a row looking selected.
const ROW_CLASS = [
    "group/row flex min-h-8 select-none items-start rounded text-sm leading-5 [-webkit-tap-highlight-color:transparent]",
    "[--tree-row-fg:var(--colorNeutralForeground1)] [--tree-row-fg-muted:var(--colorNeutralForeground3)]",
    "[@media(hover:hover)]:hover:bg-[var(--colorSubtleBackgroundHover)] [@media(hover:hover)]:hover:[--tree-row-fg:var(--colorNeutralForeground2Hover)] [@media(hover:hover)]:hover:[--tree-row-fg-muted:var(--colorNeutralForeground3Hover)]",
    "has-[[data-open=true]]:bg-[var(--colorSubtleBackgroundHover)] has-[[data-open=true]]:[--tree-row-fg:var(--colorNeutralForeground2Hover)] has-[[data-open=true]]:[--tree-row-fg-muted:var(--colorNeutralForeground3Hover)]",
    // Windows contrast themes replace backgrounds, so the highlight becomes an outline there.
    "forced-colors:hover:outline forced-colors:hover:outline-1 forced-colors:has-[[data-open=true]]:outline forced-colors:has-[[data-open=true]]:outline-1",
    // Focus ring for the keyboard focused item (the treeitem is the row's parent). Like Fluent, a second inner ring
    // keeps it visible on a highlighted row, which in high contrast has the focus colour.
    "[:focus-visible>&]:outline [:focus-visible>&]:outline-2 [:focus-visible>&]:outline-[color:var(--colorStrokeFocus2)] [:focus-visible>&]:shadow-[inset_0_0_0_1px_var(--colorStrokeFocus1)]",
].join(" ");

const CHEVRON_SLOT_CLASS = "flex h-8 w-6 shrink-0 items-center justify-center text-[color:var(--tree-row-fg-muted)]";
const CHEVRON_CLASS = "transition-transform duration-200 ease-[cubic-bezier(0.8,0,0.2,1)] motion-reduce:transition-none";

// Long labels wrap, and the chevron and actions stay level with the first line.
const LABEL_CLASS = "min-w-0 flex-1 break-words px-0.5 py-1.5 text-[color:var(--tree-row-fg)]";
const MUTED_LABEL_CLASS = "min-w-0 flex-1 break-words px-0.5 py-1.5 text-[color:var(--tree-row-fg-muted)] forced-colors:text-[GrayText] forced-colors:[forced-color-adjust:none]";

// Always takes its width; only the opacity changes, so showing the actions never reflows the label. Always shown on
// touch screens, which can't hover.
const ACTIONS_SLOT_CLASS = [
    "flex h-8 w-8 shrink-0 items-center justify-center opacity-0 transition-opacity duration-100 motion-reduce:transition-none",
    "[@media(hover:hover)]:group-hover/row:opacity-100 [@media(hover:none)]:opacity-100",
    "has-[:focus-visible]:opacity-100 has-[[data-open=true]]:opacity-100 [:focus-visible>div>&]:opacity-100",
].join(" ");

/** A vertical guide line under the chevron of the subtree's parent, painted below the rows. */
const guideStyle = (parentDepth: number, indent: number): React.CSSProperties => ({
    backgroundImage: "linear-gradient(var(--colorNeutralStroke2), var(--colorNeutralStroke2))",
    backgroundSize: "1px 100%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: `${ROW_START_PADDING + parentDepth * indent + CHEVRON_CENTER}px 0px`,
});

/**
 * Tree following the WAI-ARIA tree pattern (roving tabindex, arrow keys, Home/End, Enter/Space, type-ahead),
 * styled like Fluent's Tree with TreeItemLayout, plus indent guide lines.
 */
export function TreeView<T>({
    items,
    getId,
    getChildren,
    renderLabel,
    isMuted,
    renderActions,
    openItems: controlledOpenItems,
    onOpenChange,
    indent = 20,
    ariaLabel,
    className,
}: TreeViewProps<T>) {
    const [uncontrolledOpenItems, setUncontrolledOpenItems] = React.useState<ReadonlySet<string>>(() => new Set<string>());
    const openItems = controlledOpenItems ?? uncontrolledOpenItems;
    const [focusedKey, setFocusedKey] = React.useState<string>();
    // Where the focused row was, so the row that takes its place gets the tab stop when it is removed.
    const focusedIndex = React.useRef(0);
    const itemElements = React.useRef(new Map<string, HTMLLIElement>());
    const treeRef = React.useRef<HTMLUListElement>(null);
    const parentRestoreFocus = React.useContext(FocusRestoreContext);
    const baseId = React.useId();

    const setOpen = (id: string, open: boolean) => {
        if (openItems.has(id) === open) {
            return;
        }

        const next = new Set(Array.from(openItems));
        if (open) {
            next.add(id);
        } else {
            next.delete(id);
        }

        if (!controlledOpenItems) {
            setUncontrolledOpenItems(next);
        }
        onOpenChange?.(next);
    };

    // Rows are keyed by their path of sibling keys rather than by id, so repeated ids can't mix rows up. A sibling key
    // is the id, with a counter for repeats among the siblings.
    const siblingKeys = (siblings: T[], parent: T | undefined, depth: number) => {
        const seen = new Map<string, number>();
        return siblings.map((sibling) => {
            const id = getId(sibling, parent, depth);
            const count = seen.get(id) ?? 0;
            seen.set(id, count + 1);
            return { id, key: count === 0 ? id : `${id}#${count}` };
        });
    };
    const childKey = (parentKey: string | undefined, key: string) => (parentKey === undefined ? key : `${parentKey}\u001f${key}`);

    // Items in display order, skipping children of collapsed items. Drives keyboard navigation and the tab stop.
    const visibleItems: VisibleItem[] = [];
    const collectVisible = (siblings: T[], parent: T | undefined, depth: number, parentKey?: string) => {
        const keys = siblingKeys(siblings, parent, depth);
        siblings.forEach((item, index) => {
            const { id } = keys[index];
            const key = childKey(parentKey, keys[index].key);
            const children = getChildren?.(item) ?? [];
            visibleItems.push({ key, id, parentKey, hasChildren: children.length > 0 });
            if (children.length > 0 && openItems.has(id)) {
                collectVisible(children, item, depth + 1, key);
            }
        });
    };
    collectVisible(items, undefined, 0);

    const visibleIndex = (key: string | undefined) => visibleItems.findIndex((item) => item.key === key);
    const tabStopKey = visibleIndex(focusedKey) >= 0
        ? focusedKey
        : visibleItems[Math.min(focusedIndex.current, visibleItems.length - 1)]?.key;

    const setFocused = (key: string) => {
        setFocusedKey(key);
        focusedIndex.current = Math.max(visibleIndex(key), 0);
    };

    const focusItem = (key: string | undefined) => {
        if (key === undefined) {
            return;
        }
        setFocused(key);
        itemElements.current.get(key)?.focus();
    };

    const restoreFocus = React.useCallback(() => {
        const tabStop = treeRef.current?.querySelector<HTMLElement>('[role="treeitem"][tabindex="0"]');
        if (tabStop) {
            tabStop.focus();
        } else {
            parentRestoreFocus?.();
        }
    }, [parentRestoreFocus]);

    const labelText = (key: string) =>
        itemElements.current.get(key)?.querySelector("[data-tree-label]")?.textContent?.trim().toLowerCase() ?? "";

    const handleKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
        // Only keys pressed on a tree item itself, not on content inside a row such as the actions menu.
        const target = event.target as HTMLElement;
        const key = target.getAttribute("data-tree-key");
        if (key === null || target.getAttribute("role") !== "treeitem") {
            return;
        }

        const index = visibleIndex(key);
        const current = visibleItems[index];
        if (!current) {
            return;
        }
        const isOpen = openItems.has(current.id);

        switch (event.key) {
            case "ArrowDown":
                focusItem(visibleItems[index + 1]?.key);
                break;
            case "ArrowUp":
                focusItem(visibleItems[index - 1]?.key);
                break;
            case "ArrowRight":
                if (current.hasChildren && !isOpen) {
                    setOpen(current.id, true);
                } else if (current.hasChildren) {
                    focusItem(visibleItems[index + 1]?.key);
                }
                break;
            case "ArrowLeft":
                if (current.hasChildren && isOpen) {
                    setOpen(current.id, false);
                } else {
                    focusItem(current.parentKey);
                }
                break;
            case "Home":
                focusItem(visibleItems[0]?.key);
                break;
            case "End":
                focusItem(visibleItems[visibleItems.length - 1]?.key);
                break;
            case "Enter":
            case " ":
                if (current.hasChildren) {
                    setOpen(current.id, !isOpen);
                }
                break;
            default: {
                // Type-ahead: focus the next item whose label starts with the typed character.
                if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) {
                    return;
                }
                const character = event.key.toLowerCase();
                const candidates = visibleItems.slice(index + 1).concat(visibleItems.slice(0, index));
                const match = candidates.find((item) => labelText(item.key).startsWith(character));
                if (!match) {
                    return;
                }
                focusItem(match.key);
            }
        }

        event.preventDefault();
        // Handled here, so page-wide shortcuts (the tour moves on with the arrow keys) must not act on it too.
        event.stopPropagation();
    };

    let rowIndex = 0;
    const renderItems = (siblings: T[], parent: T | undefined, depth: number, parentKey?: string): React.ReactNode => {
        const keys = siblingKeys(siblings, parent, depth);

        return siblings.map((item, index) => {
            const { id } = keys[index];
            const key = childKey(parentKey, keys[index].key);
            const labelId = `${baseId}-${rowIndex++}`;
            const children = getChildren?.(item) ?? [];
            const hasChildren = children.length > 0;
            const isOpen = hasChildren && openItems.has(id);
            const actions = renderActions?.(item, parent, depth);
            const hasActions = actions !== undefined && actions !== null && actions !== false;

            return (
                <li
                    key={keys[index].key}
                    ref={(element) => {
                        if (element) {
                            itemElements.current.set(key, element);
                        } else {
                            itemElements.current.delete(key);
                        }
                    }}
                    // ARIA 1.2 only needs aria-selected on selectable trees; "not selected" on every item would just be noise.
                    // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                    role="treeitem"
                    aria-level={depth + 1}
                    aria-expanded={hasChildren ? isOpen : undefined}
                    aria-labelledby={labelId}
                    data-tree-id={id}
                    data-tree-key={key}
                    tabIndex={key === tabStopKey ? 0 : -1}
                    className="outline-none"
                    onFocus={(event) => {
                        if (event.target === event.currentTarget) {
                            setFocused(key);
                        }
                    }}
                >
                    <div
                        className={`${ROW_CLASS} ${hasChildren ? "cursor-pointer" : ""}`}
                        style={{ paddingLeft: ROW_START_PADDING + depth * indent }}
                        onClick={hasChildren ? () => setOpen(id, !isOpen) : undefined}
                        // Focus on the row's actions makes this the active item, so its actions stay tabbable.
                        onFocus={() => setFocused(key)}
                    >
                        <span className={CHEVRON_SLOT_CLASS} aria-hidden>
                            {hasChildren && <ChevronRight12Regular className={`${CHEVRON_CLASS} ${isOpen ? "rotate-90" : ""}`} />}
                        </span>
                        <div id={labelId} data-tree-label className={isMuted?.(item, parent, depth) ? MUTED_LABEL_CLASS : LABEL_CLASS}>
                            {renderLabel(item, parent, depth)}
                        </div>
                        {renderActions && (
                            <div data-actions className={ACTIONS_SLOT_CLASS} onClick={hasActions ? stopPropagation : undefined}>
                                <TreeActionsTabbableContext.Provider value={key === tabStopKey}>
                                    {actions}
                                </TreeActionsTabbableContext.Provider>
                            </div>
                        )}
                    </div>
                    {isOpen && (
                        <ul role="group" className="flex flex-col gap-0.5 pt-0.5" style={guideStyle(depth, indent)}>
                            {renderItems(children, item, depth + 1, key)}
                        </ul>
                    )}
                </li>
            );
        });
    };

    return (
        <FocusRestoreContext.Provider value={restoreFocus}>
            <ul ref={treeRef} role="tree" aria-label={ariaLabel} className={`flex flex-col gap-0.5 ${className ?? ""}`} onKeyDown={handleKeyDown}>
                {renderItems(items, undefined, 0)}
            </ul>
        </FocusRestoreContext.Provider>
    );
}
